import { useEffect, useRef } from 'react';
import type { WorkspaceBillingActivityType } from '@nest-react/domain';
import { useShallow } from 'zustand/react/shallow';

import { emptyBillingActivitySnapshot, emptyBillingSnapshot } from './billingTypes';
import { useWorkspaceStore } from './workspaceStore';

const EMPTY_BILLING_SNAPSHOT = emptyBillingSnapshot();
const EMPTY_ACTIVITY_SNAPSHOT = emptyBillingActivitySnapshot();

export const useWorkspaceWallet = (
  workspaceId?: string | null
): {
  balance: string | null;
  loading: boolean;
  refresh: () => void;
} => {
  const snapshot = useWorkspaceStore(
    useShallow(state =>
      workspaceId
        ? (state.billingByWorkspace[workspaceId] ?? EMPTY_BILLING_SNAPSHOT)
        : EMPTY_BILLING_SNAPSHOT
    )
  );
  const fetchWorkspaceWallet = useWorkspaceStore(
    state => state.fetchWorkspaceWallet
  );

  useEffect(() => {
    if (!workspaceId) return;
    void fetchWorkspaceWallet(workspaceId);
  }, [fetchWorkspaceWallet, workspaceId]);

  return {
    balance: snapshot.balance,
    loading: snapshot.walletLoading,
    refresh: () => {
      if (!workspaceId) return;
      void fetchWorkspaceWallet(workspaceId);
    },
  };
};

export const useWorkspaceBillingSummary = (
  workspaceId?: string | null
): {
  summary: (typeof EMPTY_BILLING_SNAPSHOT)['summary'];
  loading: boolean;
  error: string | null;
  refresh: () => void;
} => {
  const snapshot = useWorkspaceStore(
    useShallow(state =>
      workspaceId
        ? (state.billingByWorkspace[workspaceId] ?? EMPTY_BILLING_SNAPSHOT)
        : EMPTY_BILLING_SNAPSHOT
    )
  );
  const fetchWorkspaceBillingSummary = useWorkspaceStore(
    state => state.fetchWorkspaceBillingSummary
  );

  useEffect(() => {
    if (!workspaceId) return;
    void fetchWorkspaceBillingSummary(workspaceId);
  }, [fetchWorkspaceBillingSummary, workspaceId]);

  return {
    summary: snapshot.summary,
    loading: snapshot.summaryLoading,
    error: snapshot.summaryError,
    refresh: () => {
      if (!workspaceId) return;
      void fetchWorkspaceBillingSummary(workspaceId);
    },
  };
};

export const useWorkspaceBillingActivity = (
  workspaceId: string | undefined,
  options: {
    type: WorkspaceBillingActivityType | null;
    cursor: string | null;
    enabled?: boolean;
    onInvalidCursor?: () => void;
  }
): {
  page: (typeof EMPTY_BILLING_SNAPSHOT)['activity']['page'];
  loading: boolean;
  error: string | null;
} => {
  const enabled = options.enabled ?? true;
  const onInvalidCursorRef = useRef(options.onInvalidCursor);
  onInvalidCursorRef.current = options.onInvalidCursor;
  const snapshot = useWorkspaceStore(
    useShallow(state => {
      if (!workspaceId) return EMPTY_ACTIVITY_SNAPSHOT;
      return (
        state.billingByWorkspace[workspaceId]?.activity ??
        EMPTY_ACTIVITY_SNAPSHOT
      );
    })
  );
  const fetchWorkspaceBillingActivity = useWorkspaceStore(
    state => state.fetchWorkspaceBillingActivity
  );

  useEffect(() => {
    if (!workspaceId || !enabled) return undefined;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void fetchWorkspaceBillingActivity(workspaceId, {
        type: options.type,
        cursor: options.cursor,
      }).then(result => {
        if (cancelled || result !== 'invalid-cursor') return;
        onInvalidCursorRef.current?.();
      });
    }, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    enabled,
    fetchWorkspaceBillingActivity,
    options.cursor,
    options.type,
    workspaceId,
  ]);

  const requestMatches =
    snapshot.type === options.type && snapshot.cursor === options.cursor;

  return {
    page: requestMatches ? snapshot.page : null,
    loading: enabled && (snapshot.loading || !requestMatches),
    error: requestMatches ? snapshot.error : '',
  };
};
